// SEROTA Operasyon (servis firması paneli) çekirdeği (C01–C08): genel bakış, canlı operasyon, seferler, sefer detayı, müdahale,
// uyarı merkezi. Girdiler Zod, yanıtlar tip; Business (B01–B04) aynı uçları kiracı katmanıyla salt okunur kullanır.
import { z } from 'zod';
import type { BoardingMethod, BoardingType, Direction, TripStatus } from '../enums';
import type { TripDisplay } from '../state';

// ───────── uyarılar (C06) ─────────

export const ALERT_SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];
export const ALERT_STATUSES = ['OPEN', 'ACK', 'RESOLVED'] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];
export const ALERT_STATUS_LABELS: Record<AlertStatus, string> = {
  OPEN: 'Açık',
  ACK: 'Üstlenildi',
  RESOLVED: 'Çözüldü',
};
export const ALERT_SEVERITY_LABELS: Record<AlertSeverity, string> = {
  INFO: 'Bilgi',
  WARNING: 'Uyarı',
  CRITICAL: 'Kritik',
};
export const ALERT_TYPE_LABELS: Record<string, string> = {
  DELAY: 'Gecikme',
  GPS_LOST: 'GPS kaybı',
  BREAKDOWN: 'Araç arızası',
  INCIDENT: 'Olay',
  VEHICLE_CHECK: 'Araç kontrolü',
  DOC_EXPIRING: 'Belge süresi',
  DOC_UPLOADED: 'Belge onayı',
  LOW_OCCUPANCY: 'Düşük doluluk',
  MAINTENANCE_DUE: 'Bakım',
};

export interface AlertItem {
  id: string;
  type: string;
  severity: AlertSeverity;
  status: AlertStatus;
  title: string;
  detail: string | null;
  action: string | null;
  tripId: string | null;
  vehicleId: string | null;
  routeCode: string | null;
  plate: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

export const AlertUpdateInput = z.object({
  status: z.enum(['ACK', 'RESOLVED', 'OPEN']),
  note: z.string().trim().max(300).optional(),
});

export const AlertQuery = z.object({
  status: z.enum(['open', 'resolved', 'all']).default('open'),
  severity: z.enum(ALERT_SEVERITIES).optional(),
  type: z.string().max(40).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});

// ───────── genel bakış (C01) ─────────

export interface OpsKpis {
  activeVehicles: number;
  totalVehicles: number;
  notStarted: number;
  passengersToday: number;
  /** geçen haftanın aynı gününe göre yüzde değişim */
  passengersDeltaPct: number | null;
  onTimePct: number | null;
  onTimeDeltaPts: number | null;
  criticalAlerts: number;
  criticalSummary: string;
  completed: number;
  totalTrips: number;
  noShowPct: number | null;
  noShowDeltaPts: number | null;
}

export interface UpcomingTrip {
  id: string;
  plannedStartAt: string;
  direction: Direction;
  routeCode: string;
  routeName: string;
  institution: string;
  plate: string;
  driver: string;
  guide: string | null;
  assigned: number;
  capacity: number;
  display: TripDisplay;
}

export interface OpsOverview {
  company: string;
  date: string;
  now: string;
  kpis: OpsKpis;
  alerts: AlertItem[];
  upcoming: UpcomingTrip[];
}

// ───────── canlı operasyon (C02/C03) ─────────

export interface OpsLiveItem {
  tripId: string;
  vehicleId: string;
  plate: string;
  routeCode: string;
  routeName: string;
  institution: string;
  direction: Direction;
  driver: string;
  guide: string | null;
  status: TripStatus;
  display: TripDisplay;
  delayMin: number;
  plannedStartAt: string;
  etaEnd: string | null;
  occupancy: { boarded: number; capacity: number };
  progress: number;
  position: { lat: number; lng: number; heading: number; at: string | null } | null;
}

export interface OpsLive {
  date: string;
  counts: { all: number; active: number; delayed: number; critical: number; completed: number; notStarted: number };
  items: OpsLiveItem[];
}

// ───────── seferler (C07/C08) ─────────

export const TripListQuery = z.object({
  range: z.enum(['today', 'upcoming', 'past']).default('today'),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  display: z.enum(['NOT_STARTED', 'PREPARING', 'ON_TIME', 'DELAYED', 'CRITICAL', 'COMPLETED', 'CANCELLED']).optional(),
  direction: z.enum(['OUTBOUND', 'INBOUND']).optional(),
  institutionId: z.string().optional(),
  routeId: z.string().optional(),
  vehicleId: z.string().optional(),
  driverId: z.string().optional(),
  q: z.string().trim().max(80).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type TripListQuery = z.infer<typeof TripListQuery>;

export interface TripRow {
  id: string;
  date: string;
  direction: Direction;
  status: TripStatus;
  display: TripDisplay;
  routeId: string;
  routeCode: string;
  routeName: string;
  institution: string;
  vehicleId: string;
  plate: string;
  driverId: string;
  driver: string;
  guide: string | null;
  plannedStartAt: string;
  plannedEndAt: string;
  actualStartAt: string | null;
  actualEndAt: string | null;
  delayMin: number;
  boarded: number;
  assigned: number;
  capacity: number;
  distanceKm: number | null;
}

// ───────── sefer detayı (C04) ─────────

export interface TripDetailStop {
  stopId: string;
  order: number;
  name: string;
  kind: string;
  lat: number;
  lng: number;
  plannedAt: string;
  etaAt: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  skipped: boolean;
  /** dakika (+ geç, − erken), varış yoksa tahmine göre */
  deviationMin: number | null;
  assigned: number;
  boarded: number;
  absent: number;
}

export interface TripDetailPassenger {
  id: string;
  name: string;
  kind: string;
  stopId: string | null;
  stopName: string | null;
  absent: boolean;
  ready: boolean;
  status: BoardingType | null;
  method: BoardingMethod | null;
  at: string | null;
  viaReservation: boolean;
}

export interface TripTimelineEvent {
  at: string;
  kind: 'START' | 'STOP' | 'BOARDING' | 'INCIDENT' | 'ALERT' | 'INTERVENTION' | 'END' | 'CHECK';
  title: string;
  detail: string | null;
  tone: 'ok' | 'warn' | 'crit' | 'neutral' | 'route';
}

export interface InterventionItem {
  id: string;
  type: string;
  label: string;
  summary: string;
  by: string | null;
  notified: number;
  at: string;
}

export interface TripDetail extends TripRow {
  institutionId: string;
  vehicle: { id: string; plate: string; make: string; model: string; capacity: number };
  driverPhoneMasked: string | null;
  guideId: string | null;
  position: { lat: number; lng: number; heading: number; at: string | null } | null;
  plannedPolyline: string;
  actualPolyline: string | null;
  stops: TripDetailStop[];
  passengers: TripDetailPassenger[];
  alerts: AlertItem[];
  interventions: InterventionItem[];
  timeline: TripTimelineEvent[];
  check: { ok: boolean; issues: string[]; at: string } | null;
  cancelReason: string | null;
}

// ───────── müdahale (C05) ─────────

export const NOTIFY_CHANNELS = ['PUSH', 'IN_APP', 'SMS'] as const;
export type NotifyChannel = (typeof NOTIFY_CHANNELS)[number];
export const NOTIFY_CHANNEL_LABELS: Record<NotifyChannel, string> = {
  PUSH: 'Push',
  IN_APP: 'Uygulama içi',
  SMS: 'SMS',
};

/** Yolculara bildir hazır şablonları (C47 ile ortak). */
export const NOTIFY_TEMPLATES = ['DELAY', 'VEHICLE_CHANGED', 'ROUTE_UPDATED', 'TRIP_CANCELLED', 'CUSTOM'] as const;
export type NotifyTemplate = (typeof NOTIFY_TEMPLATES)[number];
export const NOTIFY_TEMPLATE_LABELS: Record<NotifyTemplate, string> = {
  DELAY: 'Gecikme',
  VEHICLE_CHANGED: 'Araç değişti',
  ROUTE_UPDATED: 'Güzergâh değişti',
  TRIP_CANCELLED: 'Sefer iptal',
  CUSTOM: 'Serbest mesaj',
};

export const DELAY_REASON_KEYS = ['TRAFFIC', 'ROAD_CLOSED', 'ACCIDENT', 'BREAKDOWN', 'OTHER'] as const;
export const DELAY_REASON_LABELS: Record<(typeof DELAY_REASON_KEYS)[number], string> = {
  TRAFFIC: 'Trafik',
  ROAD_CLOSED: 'Yol kapalı',
  ACCIDENT: 'Kaza',
  BREAKDOWN: 'Araç arızası',
  OTHER: 'Diğer',
};

export const InterventionInput = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('CHANGE_VEHICLE'),
    vehicleId: z.string(),
    driverId: z.string().optional(),
    reason: z.string().trim().max(200).optional(),
  }),
  z.object({ type: z.literal('CHANGE_DRIVER'), driverId: z.string(), reason: z.string().trim().max(200).optional() }),
  z.object({ type: z.literal('CHANGE_GUIDE'), guideId: z.string().nullable() }),
  z.object({
    type: z.literal('UPDATE_ROUTE'),
    skipStopIds: z.array(z.string()).min(1).max(30),
    reason: z.string().trim().max(200).optional(),
  }),
  z.object({
    type: z.literal('CANCEL'),
    reason: z.string().trim().min(3, 'İptal sebebini yazın.').max(200),
    notify: z.boolean().default(true),
  }),
  z.object({
    type: z.literal('NOTIFY'),
    template: z.enum(NOTIFY_TEMPLATES),
    channels: z.array(z.enum(NOTIFY_CHANNELS)).min(1).max(3),
    delayMin: z.number().int().min(1).max(180).optional(),
    reason: z.enum(DELAY_REASON_KEYS).optional(),
    message: z.string().trim().max(300).optional(),
    stopIds: z.array(z.string()).max(60).optional(),
  }),
]);
export type InterventionInput = z.infer<typeof InterventionInput>;

export interface InterventionResult {
  id: string;
  notified: number;
  smsSent: number;
  message: string;
}

export interface ReplacementCandidate {
  vehicleId: string;
  plate: string;
  model: string;
  capacity: number;
  /** aracın bugünkü durumu: boşta / başka sefer bitti / bakımda */
  availability: 'IDLE' | 'FREE_AFTER' | 'BUSY';
  freeAt: string | null;
  distanceKm: number | null;
  etaMin: number | null;
  driverId: string | null;
  driver: string | null;
  /** süresi dolmuş belge (muayene, sigorta …) — atanabilir ama uyarılır */
  docIssue: string | null;
}

export interface DriverCandidate {
  driverId: string;
  name: string;
  busy: boolean;
  docsOk: boolean;
}

export interface NotifyPreview {
  title: string;
  body: string;
  recipients: number;
  smsRecipients: number;
}

// ───────── genel arama (üst bar) ─────────

export interface SearchHit {
  kind: 'vehicle' | 'driver' | 'passenger' | 'route';
  id: string;
  title: string;
  sub: string | null;
}

export interface SearchResult {
  q: string;
  hits: SearchHit[];
}
